import { AxiosInstance } from 'axios';
import {
    CreatePartnerProductionEndpointRequest,
    PartnerProductionEndpointResponse,
    PartnerProductionEndpointListResponse,
    CreateXmpieTemplateRequest,
    XmpieTemplateResponse,
    CreateXmpieTemplateVersionRequest,
    XmpieTemplateVersionResponse,
    XmpieTemplateVersionListResponse,
    ActivateXmpieTemplateVersionRequest,
    CreatePartnerRequest,
    PartnerResponse,
    PartnerListResponse,
    CreatePartnerUserRequest,
    PartnerUserResponse,
    PartnerUserListResponse,
    ApprovePartnerUserResponse,
    RejectPartnerUserRequest,
    PartnerUserStatus,
    StaffPortalAccessResponse,
} from '@/types/mailhouse';

/**
 * Mailhouse Management API service — Boostt-internal-staff-only admin
 * endpoints on mailhouse-svc (`/admin/*`, Bearer+X-User-Id authenticated via
 * MailhouseAxios — see that file's auth doc comment).
 *
 * XMPie methods mirror `mailhouse-svc/src/presentation/routers/xmpie_admin.py`.
 * There is intentionally NO `listXmpieTemplates(partnerId)` method here: the
 * backend has no `GET /admin/partners/{partner_id}/xmpie/templates` (list)
 * route as of this writing — only create (POST). Callers must track template
 * ids returned from `createXmpieTemplate` themselves, or be given one out of
 * band, to use `listXmpieTemplateVersions` / activate. See the frontend
 * task report for this gap.
 *
 * Partner-admin methods mirror
 * `mailhouse-svc/src/presentation/routers/partner_admin.py`. Note
 * `listPartnerUsers` filters only by `status` on the backend — there is no
 * `partner_id` query param, so partner-scoped filtering happens client-side.
 */
export const mailhouseApi = (axios: AxiosInstance) => ({
    // --- Partner XMPie production endpoints ---
    createPartnerProductionEndpoint: async (
        partnerId: string,
        data: CreatePartnerProductionEndpointRequest
    ) => {
        const response = await axios.post<PartnerProductionEndpointResponse>(
            `/admin/partners/${encodeURIComponent(partnerId)}/xmpie/endpoints`,
            data
        );
        return response.data;
    },

    listPartnerProductionEndpoints: async (partnerId: string) => {
        const response = await axios.get<PartnerProductionEndpointListResponse>(
            `/admin/partners/${encodeURIComponent(partnerId)}/xmpie/endpoints`
        );
        return response.data;
    },

    // --- XMPie templates ---
    createXmpieTemplate: async (partnerId: string, data: CreateXmpieTemplateRequest) => {
        const response = await axios.post<XmpieTemplateResponse>(
            `/admin/partners/${encodeURIComponent(partnerId)}/xmpie/templates`,
            data
        );
        return response.data;
    },

    // --- XMPie template versions ---
    createXmpieTemplateVersion: async (
        templateId: string,
        data: CreateXmpieTemplateVersionRequest
    ) => {
        const response = await axios.post<XmpieTemplateVersionResponse>(
            `/admin/xmpie/templates/${encodeURIComponent(templateId)}/versions`,
            data
        );
        return response.data;
    },

    listXmpieTemplateVersions: async (templateId: string) => {
        const response = await axios.get<XmpieTemplateVersionListResponse>(
            `/admin/xmpie/templates/${encodeURIComponent(templateId)}/versions`
        );
        return response.data;
    },

    activateXmpieTemplateVersion: async (
        templateId: string,
        data: ActivateXmpieTemplateVersionRequest
    ) => {
        const response = await axios.post<XmpieTemplateResponse>(
            `/admin/xmpie/templates/${encodeURIComponent(templateId)}/activate-version`,
            data
        );
        return response.data;
    },

    // --- Partner admin: partners ---
    createPartner: async (data: CreatePartnerRequest) => {
        const response = await axios.post<PartnerResponse>('/admin/partners', data);
        return response.data;
    },

    listPartners: async () => {
        const response = await axios.get<PartnerListResponse>('/admin/partners');
        return response.data;
    },

    // --- Partner admin: partner users (access requests) ---
    createPartnerUser: async (partnerId: string, data: CreatePartnerUserRequest) => {
        const response = await axios.post<PartnerUserResponse>(
            `/admin/partners/${encodeURIComponent(partnerId)}/users`,
            data
        );
        return response.data;
    },

    /**
     * `status` is optional (omit to list every partner_user regardless of
     * status). The backend has no `partner_id` filter — callers needing a
     * partner-scoped view must filter the returned list client-side.
     */
    listPartnerUsers: async (status?: PartnerUserStatus) => {
        const response = await axios.get<PartnerUserListResponse>('/admin/partner-users', {
            params: status ? { status } : undefined,
        });
        return response.data;
    },

    approvePartnerUser: async (partnerUserId: string) => {
        const response = await axios.post<ApprovePartnerUserResponse>(
            `/admin/partner-users/${encodeURIComponent(partnerUserId)}/approve`
        );
        return response.data;
    },

    rejectPartnerUser: async (partnerUserId: string, data: RejectPartnerUserRequest = {}) => {
        const response = await axios.post<PartnerUserResponse>(
            `/admin/partner-users/${encodeURIComponent(partnerUserId)}/reject`,
            data
        );
        return response.data;
    },

    revokePartnerUser: async (partnerUserId: string) => {
        const response = await axios.post<PartnerUserResponse>(
            `/admin/partner-users/${encodeURIComponent(partnerUserId)}/revoke`
        );
        return response.data;
    },

    /**
     * Mints a bearer token for the single system-owned "staff access"
     * partner_user and returns a Partner Portal URL with it already
     * embedded — open `portal_url` directly, no login screen for the
     * staff member.
     */
    getStaffPortalAccess: async () => {
        const response = await axios.post<StaffPortalAccessResponse>(
            '/admin/staff-portal-access'
        );
        return response.data;
    },
});

export default mailhouseApi;
